/**
 * @fileoverview Dashboard page
 * This page displays a list of reports with options to view, download, copy, and delete them.
 * It serves as the main interface for managing veterinary reports.
 *
 * @module Dashboard
 */

import { useState, useEffect, useMemo, useCallback, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { Copy, Download, Play, Trash2, MoreHorizontal, Search, Plus, X } from "lucide-react"
import "../assets/styles/Dashboard.css"
import { useAuth } from "../contexts/authContext"
import {
  getReportsByVeterinary,
  deleteReport,
  duplicateReportPartial,
  downloadPDF,
  getReportImages,
  duplicateReportComplete,
} from "../services/ReportService"
import { ImageContext } from "../contexts/imageContext/imageContext"
import { useTranslation } from "react-i18next"
import { formatDate } from "../utils/dates/formatDate"

/**
 * Dashboard page that displays and manages veterinary reports
 *
 * @returns {JSX.Element} Dashboard page
 */
const Dashboard = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const { getToken } = useAuth()
  const [loading, setLoading] = useState(true)
  const [downloadingReportId, setDownloadingReportId] = useState(null)
  const [continuingReportId, setContinuingReportId] = useState(null)
  const { clearImages } = useContext(ImageContext)
  const { t } = useTranslation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [reportToDelete, setReportToDelete] = useState(null)
  const [dropdownOpen, setDropdownOpen] = useState(null)
  const [copyDropdownOpen, setCopyDropdownOpen] = useState(null)

  /**
   * Normalizes status text to a translation key
   *
   * @param {string} status - Status text from the API
   * @returns {string} Translation key for the status
   */
  const getStatusKey = (status) => {
    const value = status?.toString().trim().toLowerCase()
    if (value === "in progress" || value === "en progreso") return "inProgress"
    if (value === "completed" || value === "completado") return "completed"
    return value
  }

  /**
   * Normalizes study type text to a translation key
   *
   * @param {string} type - Study type text from the API
   * @returns {string} Translation key for the study type
   */
  const getStudyTypeKey = (type) => {
    return type?.toString().trim().toLowerCase()
  }

  /**
   * Fetches reports from the API
   *
   * @returns {Promise<void>} Promise that resolves when reports are fetched
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    const token = await getToken()
    if (!token) {
      throw new Error("No authentication token found.")
    }

    try {
      const fetchedReports = await getReportsByVeterinary(token)
      setData(fetchedReports)
      console.log("Reports fetched:", fetchedReports)
    } catch (error) {
      console.error("Error fetching reports:", error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [getToken])

  /**
   * Fetch reports on page mount
   */
  useEffect(() => {
    fetchData()
  }, [fetchData])

  /**
   * Handles search input changes
   *
   * @param {string} value - Search term
   */
  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase())
  }

  /**
   * Filters data based on search term
   */
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      return Object.entries(item).some(([key, value]) => {
        if (typeof value === "string") {
          return value.toLowerCase().includes(searchTerm)
        }
        if (typeof value === "object" && value !== null) {
          return JSON.stringify(value).toLowerCase().includes(searchTerm)
        }
        return false
      })
    })
  }, [data, searchTerm])

  /**
   * Sorts data by status and date
   */
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const statusA = getStatusKey(a.status)
      const statusB = getStatusKey(b.status)
      if (statusA === "inProgress" && statusB === "completed") {
        return -1
      }
      if (statusA === "completed" && statusB === "inProgress") {
        return 1
      }
      return new Date(b.reportDate) - new Date(a.reportDate)
    })
  }, [filteredData])

  /**
   * Handles copying a report
   *
   * @param {Object} record - Report to copy
   * @param {string} copyType - Type of copy (follow-up or complete)
   * @returns {Promise<void>} Promise that resolves when the report is copied
   */
  const handleCopyReport = async (record, copyType) => {
    const token = await getToken()
    if (!token) {
      throw new Error("No authentication token found.")
    }
    try {
      if (copyType === "follow-up") {
        const report = await duplicateReportPartial(token, record.id)
        if (report) {
          showMessage(t("dashboard.copySuccess"), "success")
          fetchData()
        }
      } else {
        console.log("Copy type is not follow-up")
        const report = await duplicateReportComplete(token, record.id)
        if (report) {
          showMessage(t("dashboard.copySuccess"), "success")
          fetchData()
        }
      }
    } catch (error) {
      console.error("Error copying report:", error)
      showMessage(t("dashboard.copyError"), "error")
    } finally {
      setCopyDropdownOpen(null)
    }
  }

  /**
   * Handles downloading a completed report or continuing an in-progress report
   *
   * @param {Object} record - Report to download or continue
   * @returns {Promise<void>} Promise that resolves when the action is completed
   */
  const handleDownloadOrContinue = async (record) => {
    try {
      if (getStatusKey(record.status) === "completed") {
        const token = await getToken()
        if (!token) {
          throw new Error("No authentication token found.")
        }
        setDownloadingReportId(record.id)
        await downloadPDF(token, record.id)
        setDownloadingReportId(null)
      } else {
        setContinuingReportId(record.id)
        console.log("Continuing report for:", record.patient.name)
        console.log("Report", record)

        sessionStorage.removeItem("formData2")
        sessionStorage.removeItem("formValues")
        sessionStorage.removeItem("vertexResponse")
        sessionStorage.removeItem("regenerateCount")
        sessionStorage.removeItem("diagnostico")
        sessionStorage.removeItem("observaciones")
        sessionStorage.removeItem("observationsForm")
        sessionStorage.removeItem("solicitud")
        sessionStorage.removeItem("report")
        clearImages()

        const token = await getToken()
        if (!token) {
          throw new Error("No authentication token found.")
        }

        try {
          const images = await getReportImages(token, record.id)
          console.log("Report images:", images)
          sessionStorage.removeItem("images")
          sessionStorage.setItem("images", JSON.stringify(images))
          window.dispatchEvent(new Event("storage"))
        } catch (error) {
          console.error("Error fetching report images:", error)
          showMessage("Error al obtener las imágenes del reporte", "error")
        }

        const formValues = {
          tutor: record.patient?.guardian?.name || "",
          tutor_email: record.patient?.guardian?.email || "",
          profesional_derivante: record.requestingProfessional?.name || "",
          profesional_derivante_email: record.requestingProfessional?.email || "",
          nombreAnimal: record.patient?.name || "",
          edad: record.patient?.age?.value || "",
          edadUnidad: record.patient?.age?.unit || "",
          sexo: record.patient?.gender || "",
          especie: record.patient?.species || "",
          raza: record.patient?.breed || "",
          fecha: record.reportDate || new Date().toISOString(),
          tipo_estudio: record.type || "",
          tutor_email_disabled: false,
          profesional_email_disabled: false,
        }
        if (Object.keys(formValues).length > 0) {
          sessionStorage.setItem("formValues", JSON.stringify(formValues))
        }
        console.log(
          record.diagnosis.aiGenerated,
          record.diagnosis.finalDiagnosis,
          record.diagnosis.generatedDiagnosis,
          record.diagnosis.model,
          record.diagnosis.modified,
          record.diagnosis.prompt,
        )
        if (
          record.diagnosis &&
          (record.diagnosis.aiGenerated ||
            record.diagnosis.finalDiagnosis ||
            record.diagnosis.generatedDiagnosis ||
            record.diagnosis.model ||
            record.diagnosis.modified ||
            record.diagnosis.prompt)
        ) {
          const vertexResponse = {
            finalDiagnosis: record.diagnosis.finalDiagnosis?.replace(/^['"\s]+|[.\s'"]+$/g, "") || "",
            model: record.diagnosis.model || "",
            aiGenerated: record.diagnosis.aiGenerated || "",
            generatedDiagnosis: record.diagnosis.generatedDiagnosis || "",
            modified: record.diagnosis.modified || "",
            prompt: record.diagnosis.prompt || "",
          }
          sessionStorage.setItem("vertexResponse", JSON.stringify(vertexResponse))
        } else {
          sessionStorage.removeItem("vertexResponse")
        }

        if (record.requests || record.findings) {
          sessionStorage.setItem(
            "formData2",
            JSON.stringify({
              observations: record.findings || undefined,
              redaccion: record.requests || undefined,
            }),
          )
        }

        if (record.findings) {
          sessionStorage.setItem("observationsForm", JSON.stringify({ observations: record.findings }))
        }

        sessionStorage.setItem("report", JSON.stringify({ id: record.id }))

        navigate("/analizar")
      }
    } catch (error) {
      console.error("Error downloading or continuing report:", error)
      showMessage("Error al descargar o continuar el reporte", "error")
      setDownloadingReportId(null)
      setContinuingReportId(null)
    } finally {
      setDropdownOpen(null)
    }
  }

  /**
   * Handles deleting a report with confirmation
   *
   * @param {Object} record - Report to delete
   */
  const handleDelete = (record) => {
    setReportToDelete(record)
    setShowDeleteModal(true)
    setDropdownOpen(null)
  }

  /**
   * Confirms deletion of a report
   */
  const confirmDelete = async () => {
    try {
      const token = await getToken()
      if (!token) {
        throw new Error("No authentication token found.")
      }

      const success = await deleteReport(token, reportToDelete.id)
      if (success) {
        showMessage(t("dashboard.deleteSuccess"), "success")
        fetchData()
      }
    } catch (error) {
      console.error("Error deleting report:", error)
      showMessage(t("dashboard.deleteError"), "error")
    } finally {
      setShowDeleteModal(false)
      setReportToDelete(null)
    }
  }

  /**
   * Creates a new report by clearing session storage and navigating to analysis page
   */
  const nuevoInforme = () => {
    sessionStorage.removeItem("formData2")
    sessionStorage.removeItem("formValues")
    sessionStorage.removeItem("vertexResponse")
    sessionStorage.removeItem("regenerateCount")
    sessionStorage.removeItem("diagnostico")
    sessionStorage.removeItem("observaciones")
    sessionStorage.removeItem("observationsForm")
    sessionStorage.removeItem("solicitud")
    sessionStorage.removeItem("report")
    clearImages()
    navigate("/analizar")
  }

  /**
   * Shows a message to the user
   *
   * @param {string} content - Message content
   * @param {string} type - Message type (success, error)
   */
  const showMessage = (content, type) => {
    // This is a placeholder for the message implementation
    // In a real app, you would use a toast or notification system
    console.log(`Message (${type}): ${content}`)
  }

  /**
   * Toggles the dropdown menu for a record
   *
   * @param {string} id - Record ID
   */
  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id)
    setCopyDropdownOpen(null)
  }

  /**
   * Toggles the copy dropdown menu for a record
   *
   * @param {string} id - Record ID
   * @param {Event} e - Event object
   */
  const toggleCopyDropdown = (id, e) => {
    e.stopPropagation()
    setCopyDropdownOpen(copyDropdownOpen === id ? null : id)
  }

  /**
   * Closes all dropdowns when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen(null)
      setCopyDropdownOpen(null)
    }

    document.addEventListener("click", handleClickOutside)
    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [])

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="search-container">
            <div className="search-input-wrapper">
              <Search className="search-icon" size={20} />
              <input type="text" placeholder={t("dashboard.search")} className="search-input" onChange={handleSearch} />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm("")}>
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          <button className="mt-4 redesigned-secondary-button action-button" onClick={nuevoInforme}>
            <Plus size={18} className="button-icon" />
            {t("dashboard.createNewReport")}
          </button>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="loading-indicator">Cargando informes...</div>
          ) : sortedData.length === 0 ? (
            <div className="no-data">No se encontraron informes</div>
          ) : (
            <table className="reports-table">
              <thead>
                <tr>
                  <th>{t("dashboard.name")}</th>
                  <th>{t("dashboard.guardian")}</th>
                  <th>{t("dashboard.reportDate")}</th>
                  <th className="hide-mobile">{t("dashboard.referrer")}</th>
                  <th className="hide-mobile">{t("dashboard.studyType")}</th>
                  <th>{t("dashboard.status")}</th>
                  <th className="actions-column">{t("dashboard.action")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((record) => (
                  <tr key={record.id}>
                    <td className="patient-name">{record.patient?.name}</td>
                    <td>{record.patient?.guardian?.name}</td>
                    <td>{formatDate(record.reportDate)}</td>
                    <td className="hide-mobile">{record.requestingProfessional?.name}</td>
                    <td className="hide-mobile">{t(`dashboard.studyTypes.${getStudyTypeKey(record.type)}`)}</td>
                    <td>
                      <span
                        className={`status-tag ${
                          getStatusKey(record.status) === "inProgress" ? "status-progress" : "status-completed"
                        }`}
                      >
                        {t(`dashboard.statusLabels.${getStatusKey(record.status)}`)}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="desktop-actions">
                        <button
                          className={`action-button ${
                            getStatusKey(record.status) === "completed" ? "download-button" : "continue-button"
                          }`}
                          onClick={() => handleDownloadOrContinue(record)}
                          disabled={downloadingReportId === record.id || continuingReportId === record.id}
                        >
                          {getStatusKey(record.status) === "completed" ? (
                            <>
                              {downloadingReportId === record.id ? (
                                <span>{t("dashboard.downloading")}</span>
                              ) : (
                                <>
                                  <Download size={16} className="button-icon" /> {t("dashboard.download")}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {continuingReportId === record.id ? (
                                <span>{t("dashboard.loading")}</span>
                              ) : (
                                <>
                                  <Play size={16} className="button-icon" /> {t("dashboard.continue")}
                                </>
                              )}
                            </>
                          )}
                        </button>

                        <div className="copy-dropdown-container" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="action-button copy-button"
                            onClick={(e) => toggleCopyDropdown(record.id, e)}
                          >
                            <Copy size={16} />
                          </button>
                          {copyDropdownOpen === record.id && (
                            <div className="copy-dropdown">
                              <button className="copy-option" onClick={() => handleCopyReport(record, "complete")}>
                                {t("dashboard.copyComplete")}
                              </button>
                              <button className="copy-option" onClick={() => handleCopyReport(record, "follow-up")}>
                                {t("dashboard.copyForFollowUp")}
                              </button>
                            </div>
                          )}
                        </div>

                        <button className="action-button delete-button" onClick={() => handleDelete(record)}>
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="mobile-actions">
                        <button
                          className="mobile-menu-button"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDropdown(record.id)
                          }}
                        >
                          <MoreHorizontal size={20} />
                        </button>

                        {dropdownOpen === record.id && (
                          <div className="mobile-dropdown">
                            <button
                              className="dropdown-item"
                              onClick={() => handleDownloadOrContinue(record)}
                              disabled={downloadingReportId === record.id || continuingReportId === record.id}
                            >
                              {getStatusKey(record.status) === "completed" ? (
                                <>
                                  {downloadingReportId === record.id ? (
                                    t("dashboard.downloading")
                                  ) : (
                                    <>
                                      <Download size={16} className="dropdown-icon" /> {t("dashboard.download")}
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  {continuingReportId === record.id ? (
                                    t("dashboard.loading")
                                  ) : (
                                    <>
                                      <Play size={16} className="dropdown-icon" /> {t("dashboard.continue")}
                                    </>
                                  )}
                                </>
                              )}
                            </button>
                            <button className="dropdown-item" onClick={() => handleCopyReport(record, "complete")}>
                              <Copy size={16} className="dropdown-icon" /> {t("dashboard.copyComplete")}
                            </button>
                            <button className="dropdown-item" onClick={() => handleCopyReport(record, "follow-up")}>
                              <Copy size={16} className="dropdown-icon" /> {t("dashboard.copyForFollowUp")}
                            </button>
                            <button className="dropdown-item delete-item" onClick={() => handleDelete(record)}>
                              <Trash2 size={16} className="dropdown-icon" /> {t("dashboard.delete")}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {sortedData.length > 0 && (
            <div className="pagination">
              <span className="pagination-info">
                Mostrando 1-{Math.min(sortedData.length, 15)} de {sortedData.length} informes
              </span>
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>{t("dashboard.confirmDelete")}</h3>
            </div>
            <div className="modal-content">
              <p>{t("dashboard.cannotUndo")}</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={() => setShowDeleteModal(false)}>
                {t("rightColumn.cancel")}
              </button>
              <button className="modal-button delete-confirm-button" onClick={confirmDelete}>
                {t("dashboard.yesDelete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
